/**
 * Tailor my resume for this posting.
 *
 * The model drafts from the seeker's OWN resume: a summary aimed at the
 * posting, the experience lines to lead with, and the things the posting
 * asks for that the resume does not show. Then the seeker edits. Nothing on
 * this screen is sent anywhere by itself; "use" means copying it into their
 * own document.
 *
 * THE FLAGS ARE THE POINT. Every certification-shaped acronym, year and
 * figure in the draft was looked up in the resume, and the ones not found
 * are listed here as "not in your resume". A flagged claim is unverified,
 * not necessarily false -- the seeker decides, and the flag stays until they
 * change the text.
 *
 * Loading, empty, generating, failed and ready are different screens.
 */

import { useCallback, useEffect, useState } from "react";
import { api, apiFile, saveBlob, ApiError } from "./auth";
import { PostingReader } from "./Posting";

interface Flag {
  claim: string;
  field: "summary" | "leadWith" | "letter";
  where: string;
  /** credential: an acronym, year or figure not in the resume. posting: a word borrowed from the posting. */
  kind: "credential" | "posting";
}

interface Tailoring {
  summary: string;
  leadWith: string[];
  gaps: string[];
  flags: Flag[];
  model: string | null;
  generatedAt: string | null;
  editedAt: string | null;
  updatedAt: string;
}

interface Letter {
  greeting: string;
  paragraphs: string[];
  closing: string;
  flags: Flag[];
  model: string | null;
  generatedAt: string | null;
  editedAt: string | null;
  updatedAt: string;
}

interface TailorResponse {
  job: {
    id: string;
    title: string;
    employer: string;
    description: string;
    closedAt: string | null;
    applyUrl: string | null;
  };
  tailoring: Tailoring | null;
  /** The cover letter for this posting, if one has been drafted. */
  letter: Letter | null;
  /** Distinctive posting terms the resume lacks, from the keyword scorer. */
  gaps: string[];
  hasResume: boolean;
  usage: { used: number; limit: number };
}

function describe(err: unknown): string {
  return err instanceof ApiError ? `${err.message} (HTTP ${err.status})` : String(err);
}

function when(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function TailorScreen({ jobId, onBack }: { jobId: string; onBack: () => void }) {
  const [data, setData] = useState<TailorResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api<TailorResponse>(`/api/tailor/${jobId}`)
      .then(setData)
      .catch((err) => setError(describe(err)))
      .finally(() => setLoading(false));
  }, [jobId]);

  useEffect(load, [load]);

  const generate = () => {
    setGenerating(true);
    setGenError(null);
    api<{ tailoring: Tailoring; usage: TailorResponse["usage"] }>(`/api/tailor/${jobId}`, { method: "POST", body: "{}" })
      .then((r) => setData((d) => d && { ...d, tailoring: r.tailoring, usage: r.usage }))
      .catch((err) => setGenError(describe(err)))
      .finally(() => setGenerating(false));
  };

  if (loading) return <p className="muted">Loading the posting and your resume…</p>;

  if (error) {
    return (
      <section>
        <h1>Tailor</h1>
        <p className="err">{error}</p>
        <div className="row">
          <button className="btn" onClick={load}>Try again</button>
          <button className="btn ghost" onClick={onBack}>Back</button>
        </div>
      </section>
    );
  }

  if (!data) return null;
  const { job, tailoring, usage } = data;

  return (
    <section>
      <header className="bar">
        <div>
          <h1 className="tight">Tailor for this posting</h1>
          <p className="muted small">
            {job.title} · {job.employer}
            {job.closedAt ? " · this posting has closed" : ""}
          </p>
        </div>
        <button className="btn ghost small" onClick={onBack}>Back</button>
      </header>

      <PostingReader jobId={job.id} />

      {data.gaps.length > 0 && (
        <div className="notice">
          <p className="small"><strong>What this posting says that your resume doesn't:</strong></p>
          <p className="evidence">
            {data.gaps.map((g) => <span className="tag" key={g}>{g}</span>)}
          </p>
          <p className="muted small">
            These are the posting's distinctive words that do not appear in your resume text.
            Some you can honestly add — a skill you have but never wrote down. Some you cannot.
            A draft never claims the second kind for you.
          </p>
        </div>
      )}

      {!data.hasResume ? (
        <div className="notice">
          <p>There is no resume text to tailor from yet.</p>
          <p className="muted small">Upload and confirm a resume first; the draft is written from it and nothing else.</p>
        </div>
      ) : !tailoring ? (
        <div className="notice">
          <p>
            Draft a summary and lead-with lines for this posting, from your own resume.
          </p>
          <p className="muted small">
            The model is told to use only facts in your resume — and then the draft is checked
            against your resume anyway: every certification, year and figure, and every word it
            borrowed from the posting that your resume never uses. Anything unbacked is flagged
            for you to judge. You edit before you use any of it.
          </p>
          <div className="row">
            <button className="btn" disabled={generating || usage.used >= usage.limit} onClick={generate}>
              {generating ? "Drafting…" : "Draft it"}
            </button>
            <span className="muted small saved-meta">{usage.limit - usage.used} of {usage.limit} drafts left today</span>
          </div>
          {genError && <p className="err small">{genError}</p>}
        </div>
      ) : (
        <Editor
          jobId={jobId}
          tailoring={tailoring}
          usage={usage}
          generating={generating}
          genError={genError}
          onRegenerate={generate}
          onSaved={(t) => setData((d) => d && { ...d, tailoring: t })}
        />
      )}

      {data.hasResume && (
        <CoverLetter
          jobId={jobId}
          letter={data.letter}
          usage={usage}
          hasTailoring={tailoring !== null}
          onChange={(letter, u) => setData((d) => d && { ...d, letter, usage: u ?? d.usage })}
        />
      )}

      <div className="row">
        {job.applyUrl && (
          <a className="btn ghost" href={job.applyUrl} target="_blank" rel="noreferrer noopener">Open the posting</a>
        )}
      </div>
    </section>
  );
}

/**
 * The cover letter for this posting. Same material as the resume draft --
 * the resume, the experience, the posting, the honest gap list -- and the
 * same check afterwards. The letter may name the employer and the role; it
 * may not claim anything the resume does not show, and each gap it mentions
 * it must call a gap.
 */
function CoverLetter({
  jobId,
  letter,
  usage,
  hasTailoring,
  onChange,
}: {
  jobId: string;
  letter: Letter | null;
  usage: { used: number; limit: number };
  hasTailoring: boolean;
  onChange: (letter: Letter, usage?: { used: number; limit: number }) => void;
}) {
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [greeting, setGreeting] = useState(letter?.greeting ?? "");
  const [body, setBody] = useState(letter?.paragraphs.join("\n\n") ?? "");
  const [closing, setClosing] = useState(letter?.closing ?? "");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // A regeneration replaces the text under the editor.
  useEffect(() => {
    setGreeting(letter?.greeting ?? "");
    setBody(letter?.paragraphs.join("\n\n") ?? "");
    setClosing(letter?.closing ?? "");
    setSavedAt(null);
  }, [letter?.generatedAt, letter?.greeting, letter?.paragraphs, letter?.closing]);

  const generate = () => {
    setGenerating(true);
    setGenError(null);
    api<{ letter: Letter; usage: { used: number; limit: number } }>(`/api/cover/${jobId}`, { method: "POST", body: "{}" })
      .then((r) => onChange(r.letter, r.usage))
      .catch((err) => setGenError(describe(err)))
      .finally(() => setGenerating(false));
  };

  const dirty = letter !== null && (
    greeting !== letter.greeting || body !== letter.paragraphs.join("\n\n") || closing !== letter.closing
  );

  const save = () => {
    setSaving(true);
    setSaveError(null);
    api<{ letter: Letter }>(`/api/cover/${jobId}`, {
      method: "PUT",
      body: JSON.stringify({ greeting, paragraphs: body, closing }),
    })
      .then((r) => { onChange(r.letter); setSavedAt(new Date().toISOString()); })
      .catch((err) => setSaveError(describe(err)))
      .finally(() => setSaving(false));
  };

  const copy = () => {
    setCopied(false);
    const text = [greeting, "", body.trim(), "", closing].join("\n") + "\n";
    navigator.clipboard?.writeText(text).then(() => setCopied(true)).catch(() => setCopied(false));
  };

  const download = () => {
    setDownloading(true);
    setDownloadError(null);
    apiFile(`/api/cover/${jobId}/docx`)
      .then(({ blob, filename }) => saveBlob(blob, filename))
      .catch((err) => setDownloadError(describe(err)))
      .finally(() => setDownloading(false));
  };

  const left = usage.limit - usage.used;

  return (
    <section>
      <h2>Cover letter</h2>

      {!letter ? (
        <div className="notice">
          <p>A short letter for this posting, from your own resume — and it says what you have <em>not</em> done.</p>
          <p className="muted small">
            Three paragraphs: the role and what in your record answers it, the specifics that bear on it,
            and the gaps handled honestly. {hasTailoring
              ? "It uses the gap list from your resume draft above."
              : "Draft the resume first and it will use that draft's gap list; otherwise it works from the posting's terms."}{" "}
            Then it is checked the same way: every credential, year, figure and borrowed word, flagged if your resume does not back it.
          </p>
          <div className="row">
            <button className="btn" disabled={generating || left <= 0} onClick={generate}>
              {generating ? "Writing…" : "Write the letter"}
            </button>
            <span className="muted small saved-meta">{left} of {usage.limit} drafts left today</span>
          </div>
          {genError && <p className="err small">{genError}</p>}
        </div>
      ) : (
        <div>
          <p className="muted small">
            Written {when(letter.generatedAt)}
            {letter.editedAt ? ` · edited ${when(letter.editedAt)}` : ""}
            {letter.model ? ` · ${letter.model.replace(/^@cf\//, "")}` : ""}
          </p>

          {letter.flags.length > 0 ? (
            <div className="notice flagged">
              <p className="small">
                <strong>Not backed by your resume:</strong> {letter.flags.length} thing{letter.flags.length === 1 ? "" : "s"} in this letter.{" "}
                A credential, year or figure your resume does not contain, or a word the employer uses that your resume never does.
                Delete each one, or keep it only if it is true and you can back it up.
              </p>
              <ul className="bindings">
                {letter.flags.map((f, i) => (
                  <li key={i}>
                    <strong>{f.claim}</strong>
                    <span className="muted"> — {f.kind === "posting" ? "borrowed from the posting" : "not in your resume"}: “{f.where}”</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="badge">Every credential, year, figure and posting term in this letter is backed by your resume</p>
          )}

          <div className="field">
            <label className="field-label" htmlFor="cover-greeting">Greeting</label>
            <input id="cover-greeting" className="input" value={greeting} onChange={(e) => setGreeting(e.target.value)} />
          </div>

          <div className="field">
            <div className="bar slim">
              <label className="field-label" htmlFor="cover-body">The letter, one blank line between paragraphs</label>
              <button className="step-link small" type="button" onClick={copy}>{copied ? "Copied" : "Copy the letter"}</button>
            </div>
            <textarea id="cover-body" className="input notes" rows={14} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="cover-closing">Closing</label>
            <p className="field-hint">Your name is added under it in the Word file.</p>
            <input id="cover-closing" className="input" value={closing} onChange={(e) => setClosing(e.target.value)} />
          </div>

          <div className="row">
            <button className="btn" disabled={saving || !dirty} onClick={save}>{saving ? "Saving…" : "Save my edits"}</button>
            <button className="btn ghost" disabled={generating || left <= 0} onClick={generate}>
              {generating ? "Writing…" : "Write it again"}
            </button>
            <span className="muted small saved-meta">
              {savedAt ? `Saved ${when(savedAt)}` : `${left} of ${usage.limit} drafts left today`}
            </span>
          </div>
          {saveError && <p className="err small">{saveError}</p>}
          {genError && <p className="err small">{genError}</p>}

          <div className="notice">
            <p className="small"><strong>The letter as a Word file.</strong> Your name and contact details on top, today's date, the role and employer, then the letter as it is above — one column, plain paragraphs.</p>
            <div className="row">
              <button className="btn ghost" disabled={downloading || dirty} onClick={download}>
                {downloading ? "Building…" : "Download .docx"}
              </button>
              {dirty && <span className="muted small saved-meta">Save your edits first — the file is built from the saved letter.</span>}
            </div>
            {downloadError && <p className="err small">{downloadError}</p>}
          </div>
        </div>
      )}
    </section>
  );
}

function Editor({
  jobId,
  tailoring,
  usage,
  generating,
  genError,
  onRegenerate,
  onSaved,
}: {
  jobId: string;
  tailoring: Tailoring;
  usage: { used: number; limit: number };
  generating: boolean;
  genError: string | null;
  onRegenerate: () => void;
  onSaved: (t: Tailoring) => void;
}) {
  const [summary, setSummary] = useState(tailoring.summary);
  const [leadWith, setLeadWith] = useState(tailoring.leadWith.join("\n"));
  const [gaps, setGaps] = useState(tailoring.gaps.join("\n"));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const download = () => {
    setDownloading(true);
    setDownloadError(null);
    apiFile(`/api/tailor/${jobId}/docx`)
      .then(({ blob, filename }) => saveBlob(blob, filename))
      .catch((err) => setDownloadError(describe(err)))
      .finally(() => setDownloading(false));
  };

  // A regeneration replaces the text under the editor.
  useEffect(() => {
    setSummary(tailoring.summary);
    setLeadWith(tailoring.leadWith.join("\n"));
    setGaps(tailoring.gaps.join("\n"));
    setSavedAt(null);
  }, [tailoring.generatedAt, tailoring.summary, tailoring.leadWith, tailoring.gaps]);

  const dirty =
    summary !== tailoring.summary
    || leadWith !== tailoring.leadWith.join("\n")
    || gaps !== tailoring.gaps.join("\n");

  const lines = (s: string) => s.split("\n").map((l) => l.trim()).filter(Boolean);

  const save = () => {
    setSaving(true);
    setSaveError(null);
    api<{ tailoring: Tailoring }>(`/api/tailor/${jobId}`, {
      method: "PUT",
      body: JSON.stringify({ summary, lead_with: lines(leadWith), gaps: lines(gaps) }),
    })
      .then((r) => { onSaved(r.tailoring); setSavedAt(new Date().toISOString()); })
      .catch((err) => setSaveError(describe(err)))
      .finally(() => setSaving(false));
  };

  const copy = (label: string, text: string) => {
    setCopied(null);
    navigator.clipboard?.writeText(text)
      .then(() => setCopied(label))
      .catch(() => setCopied(null));
  };

  const flagsIn = (field: Flag["field"]) => tailoring.flags.filter((f) => f.field === field);
  const credentialFlags = tailoring.flags.filter((f) => f.kind !== "posting");
  const postingFlags = tailoring.flags.filter((f) => f.kind === "posting");

  return (
    <div>
      <p className="muted small">
        Drafted {when(tailoring.generatedAt)}
        {tailoring.editedAt ? ` · edited ${when(tailoring.editedAt)}` : ""}
        {tailoring.model ? ` · ${tailoring.model.replace(/^@cf\//, "")}` : ""}
      </p>

      {tailoring.flags.length > 0 ? (
        <div className="notice flagged">
          {credentialFlags.length > 0 && (
            <>
              <p className="small">
                <strong>Not in your resume:</strong> {credentialFlags.length} credential{credentialFlags.length === 1 ? "" : "s"}, year{credentialFlags.length === 1 ? "" : "s"} or figure{credentialFlags.length === 1 ? "" : "s"}.{" "}
                Delete each one, or keep it only if it is true and you can back it up.
              </p>
              <ul className="bindings">
                {credentialFlags.map((f, i) => (
                  <li key={`c${i}`}>
                    <strong>{f.claim}</strong>
                    <span className="muted"> — in the {f.field === "summary" ? "summary" : "lead-with lines"}: “{f.where}”</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          {postingFlags.length > 0 && (
            <>
              <p className="small">
                <strong>Borrowed from the posting:</strong> {postingFlags.length} word{postingFlags.length === 1 ? "" : "s"} the employer uses that your resume never does.{" "}
                The model put the employer's language in your mouth. Keep a word only if it is true of you.
              </p>
              <ul className="bindings">
                {postingFlags.map((f, i) => (
                  <li key={`p${i}`}>
                    <strong>{f.claim}</strong>
                    <span className="muted"> — in the {f.field === "summary" ? "summary" : "lead-with lines"}: “{f.where}”</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      ) : (
        <p className="badge">Every credential, year, figure and posting term in this draft is backed by your resume</p>
      )}

      <div className="field">
        <div className="bar slim">
          <label className="field-label" htmlFor="tailor-summary">Summary for the top of your resume</label>
          <button className="step-link small" type="button" onClick={() => copy("summary", summary)}>
            {copied === "summary" ? "Copied" : "Copy"}
          </button>
        </div>
        {flagsIn("summary").length > 0 && (
          <p className="field-hint err small">Check: {flagsIn("summary").map((f) => f.claim).join(", ")} — not backed by your resume.</p>
        )}
        <textarea id="tailor-summary" className="input notes" rows={6} value={summary} onChange={(e) => setSummary(e.target.value)} />
      </div>

      <div className="field">
        <div className="bar slim">
          <label className="field-label" htmlFor="tailor-lead">Lead with these, one per line</label>
          <button className="step-link small" type="button" onClick={() => copy("lead", leadWith)}>
            {copied === "lead" ? "Copied" : "Copy"}
          </button>
        </div>
        <p className="field-hint">Your own experience lines, rephrased toward the posting's language where your resume supports it.</p>
        {flagsIn("leadWith").length > 0 && (
          <p className="field-hint err small">Check: {flagsIn("leadWith").map((f) => f.claim).join(", ")} — not backed by your resume.</p>
        )}
        <textarea id="tailor-lead" className="input notes" rows={7} value={leadWith} onChange={(e) => setLeadWith(e.target.value)} />
      </div>

      <div className="field">
        <label className="field-label" htmlFor="tailor-gaps">What the posting asks for that your resume does not show</label>
        <p className="field-hint">The honest list. Address these in a cover letter or an interview — not by adding them to the resume.</p>
        <textarea id="tailor-gaps" className="input notes" rows={4} value={gaps} onChange={(e) => setGaps(e.target.value)} />
      </div>

      <div className="row">
        <button className="btn" disabled={saving || !dirty} onClick={save}>{saving ? "Saving…" : "Save my edits"}</button>
        <button className="btn ghost" disabled={generating || usage.used >= usage.limit} onClick={onRegenerate}>
          {generating ? "Drafting…" : tailoring.editedAt || dirty ? "Draft again (replaces your edits)" : "Draft again"}
        </button>
        <span className="muted small saved-meta">{usage.limit - usage.used} of {usage.limit} drafts left today</span>
        {savedAt && !dirty && <span className="saved small">Saved</span>}
      </div>
      {saveError && <p className="err small">{saveError}</p>}
      {genError && <p className="err small">{genError}</p>}

      <div className="field">
        <p className="field-label">The tailored resume as a Word file</p>
        <p className="field-hint">
          Your name and contact details on top, this summary in place of your original one, the
          lead-with lines as highlights, then your confirmed resume text as it is. One column, plain
          paragraphs, Calibri — the layout applicant tracking systems parse without losing anything.
          {dirty ? " Save your edits first; the file is built from what is saved." : ""}
        </p>
        <div className="row">
          <button className="btn" disabled={downloading || dirty} onClick={download}>
            {downloading ? "Building…" : "Download .docx"}
          </button>
        </div>
        {downloadError && <p className="err small">{downloadError}</p>}
      </div>
    </div>
  );
}
